// What a refused saved-view write says, and where it says it (spec 16 §4
// "Errors on save", §5). Pure, so the mapping is pinned without a screen.
//
// Each refusal lands in one of two places. A problem about the NAME (409
// `SAVED_VIEW_NAME_TAKEN`, or the pipe's 400 on the `name` path) is a field
// error on the name. Everything else is a form error: a full cap names the
// cap from the problem's `cap` extension and points to Manage, and D-305
// ruling 31's 400 on `config.filters.q` names the contact address the shared
// view may not keep. The server's `errors[].message_key` is the translatable
// machinery (D-022), and the one key this surface's service emits is mapped
// here by a literal, so the i18n parity gate sees it referenced. That one
// refusal has one string wherever it lands, Save as new, Save or Manage's
// share (R3, PR-E4 wave 4).
//
// Any other 400 on a `config.filters.<key>` path is a stored filter the
// server refused, and it names the filter by the key its path starts with
// (R7). The screens' validators hold the saved-view grammar, so the arm is
// the backstop for a value one of them lets through.
//
// A write made offline is refused before it is sent (W3, PR-E4 wave 5): the
// data layer throws `ViewsOfflineError`, which is one form error with the
// offline sentence wherever the write was made. Nothing is queued, since a
// view is an office preference and a write replayed later could land on a
// view the reader has since changed.
//
// A WRITE WHOSE ANSWER NEVER CAME (V2, PR-E4 wave 6). A request that left the
// browser and got no answer back, because the connection dropped or the
// browser went offline on the way, may have been applied. The data layer
// throws `ViewsResponseLostError` for it, and 409 `IDEMPOTENCY_IN_PROGRESS`
// (the server still running the first try) reads the same way. Both say the
// change may have been saved and to try again. The form that made the write
// keeps its Idempotency-Key for the retry (`write-key.ts`), so a retry of a
// write that did land replays the server's first answer instead of making a
// second view. 409 `IDEMPOTENCY_KEY_REUSE` answers a retry that changed what
// it sends after its first try landed: the earlier try is what the list now
// shows, and the sentence says so. A 5xx with no problem body is a gateway's,
// not the server's answer, and reads as a lost answer (`answeredByGateway`).

import { RequestError } from "../../../../lib/request";

/** The i18n keys a refusal renders. Literal, so `pnpm i18n:parity` can check
 * each against the dictionary. */
export type ViewErrorKey =
  | "views.errors.nameRequired"
  | "views.errors.nameTooLong"
  | "views.errors.nameInvalid"
  | "views.errors.nameTaken"
  | "views.errors.limitMember"
  | "views.errors.limitShared"
  | "views.errors.systemImmutable"
  | "views.errors.notOwner"
  | "views.errors.notFound"
  | "views.errors.readOnly"
  | "views.errors.forbidden"
  | "views.errors.failed"
  | "views.errors.filterInvalid"
  | "views.errors.offline"
  | "views.errors.mayHaveSaved"
  | "views.errors.earlierTryLanded"
  | "views.validation.contact_address_not_shareable";

export interface ViewError {
  /** `name` renders on the name field; `form` renders on the form. */
  readonly target: "name" | "form";
  readonly key: ViewErrorKey;
  /** Interpolation values for the key (the cap's size). */
  readonly params?: Readonly<Record<string, number>>;
  /** The URL key of the stored filter the server refused (R7). The line
   * renders its name, `views.filters.<key>`, into the sentence. */
  readonly filter?: string;
  /** A full cap: the form offers a way into Manage. */
  readonly pointsToManage?: boolean;
  /** The request id for the §4 error line, when the server sent one. */
  readonly requestId?: string | undefined;
}

/** A saved-view write refused because the browser is offline (W3). The data
 * layer throws it before any request, so nothing of it reached the server. */
export class ViewsOfflineError extends Error {
  constructor() {
    super("A saved-view write needs the network");
    this.name = "ViewsOfflineError";
  }
}

/** A saved-view write that left the browser and got no answer back (V2):
 * the connection dropped, or the browser went offline on the way. The server
 * may have applied it, so the form keeps its Idempotency-Key for the retry. */
export class ViewsResponseLostError extends Error {
  constructor(options?: { readonly cause?: unknown }) {
    super("A saved-view write got no answer", options);
    this.name = "ViewsResponseLostError";
  }
}

/** A 5xx that carries no problem body. The API answers every failure with a
 * problem, so a bare 5xx is a gateway's (a load balancer that timed out on the
 * API or found no healthy target), and the API may have applied the write
 * behind it. It is a lost answer, not the server's verdict (V2). */
export function answeredByGateway(error: unknown): boolean {
  return error instanceof RequestError && error.httpStatus >= 500 && error.problem === undefined;
}

/** The write failed without the server's verdict on it, so its form keeps
 * the key it sent (V2): the browser refused it offline, its answer was lost
 * (a gateway's bare 5xx included), or the server is still running an earlier
 * try under the same key. */
export function keepsIdempotencyKey(error: unknown): boolean {
  return (
    error instanceof ViewsOfflineError ||
    error instanceof ViewsResponseLostError ||
    answeredByGateway(error) ||
    (error instanceof RequestError && error.code === "IDEMPOTENCY_IN_PROGRESS")
  );
}

/** A view name is 1 to 60 characters after trimming, counted as code points,
 * the unit the database counts (spec 16 §2.20). */
export const VIEW_NAME_MAX = 60;

/** The client's own check of a name, before any request (the server checks
 * again). `undefined` when the name may be sent. */
export function nameError(name: string): ViewError | undefined {
  const trimmed = name.trim();
  if (trimmed.includes("\u0000")) return { target: "name", key: "views.errors.nameInvalid" };
  // `.` with the `u` flag matches one code point and `s` lets it match a line
  // break too: the count the contract's `SavedViewName` makes.
  const length = trimmed.match(/./gsu)?.length ?? 0;
  if (length === 0) return { target: "name", key: "views.errors.nameRequired" };
  if (length > VIEW_NAME_MAX) return { target: "name", key: "views.errors.nameTooLong" };
  return undefined;
}

const CONTACT_ADDRESS_KEY = "views.validation.contact_address_not_shareable";

/** The filter a `config.filters.<key>[.<item>]` issue path names, or
 * undefined for any other path. */
function filterOfPath(path: string): string | undefined {
  const match = /^config\.filters\.([a-z][a-z0-9_]*)(?:\.|$)/u.exec(path);
  return match?.[1];
}

/** The `cap` and `limit` extensions of 409 `SAVED_VIEW_LIMIT_REACHED`. */
function capOf(error: RequestError): { cap: "member" | "shared"; limit: number } {
  const problem = error.problem as Record<string, unknown> | undefined;
  const cap = problem?.["cap"] === "shared" ? "shared" : "member";
  const limit = typeof problem?.["limit"] === "number" ? problem["limit"] : 20;
  return { cap, limit };
}

/** Map a failed saved-view write to what the reader is told. */
export function viewErrorOf(error: unknown): ViewError {
  if (error instanceof ViewsOfflineError) return { target: "form", key: "views.errors.offline" };
  if (error instanceof ViewsResponseLostError || answeredByGateway(error)) {
    return { target: "form", key: "views.errors.mayHaveSaved" };
  }
  if (!(error instanceof RequestError)) return { target: "form", key: "views.errors.failed" };
  const requestId = error.requestId;
  switch (error.code) {
    case "IDEMPOTENCY_IN_PROGRESS":
      return { target: "form", key: "views.errors.mayHaveSaved", requestId };
    case "IDEMPOTENCY_KEY_REUSE":
      return { target: "form", key: "views.errors.earlierTryLanded", requestId };
    case "SAVED_VIEW_NAME_TAKEN":
      return { target: "name", key: "views.errors.nameTaken", requestId };
    case "SAVED_VIEW_LIMIT_REACHED": {
      const { cap, limit } = capOf(error);
      return {
        target: "form",
        key: cap === "shared" ? "views.errors.limitShared" : "views.errors.limitMember",
        params: { limit },
        pointsToManage: true,
        requestId,
      };
    }
    case "SAVED_VIEW_SYSTEM_IMMUTABLE":
      return { target: "form", key: "views.errors.systemImmutable", requestId };
    case "SAVED_VIEW_NOT_OWNER":
      return { target: "form", key: "views.errors.notOwner", requestId };
    case "NOT_FOUND":
      return { target: "form", key: "views.errors.notFound", requestId };
    case "IMPERSONATION_READ_ONLY":
      return { target: "form", key: "views.errors.readOnly", requestId };
    case "PERMISSION_DENIED":
      return { target: "form", key: "views.errors.forbidden", requestId };
    default:
      break;
  }
  if (error.httpStatus === 400) {
    if (error.fieldErrors.some((fe) => fe.message_key === CONTACT_ADDRESS_KEY)) {
      return { target: "form", key: "views.validation.contact_address_not_shareable", requestId };
    }
    const onName = error.fieldErrors.find((fe) => fe.path === "name");
    if (onName !== undefined) {
      if (onName.code === "too_big") return { target: "name", key: "views.errors.nameTooLong" };
      if (onName.code === "too_small") return { target: "name", key: "views.errors.nameRequired" };
      return { target: "name", key: "views.errors.nameInvalid" };
    }
    for (const fe of error.fieldErrors) {
      const filter = filterOfPath(fe.path);
      if (filter !== undefined) {
        return { target: "form", key: "views.errors.filterInvalid", filter, requestId };
      }
    }
  }
  return { target: "form", key: "views.errors.failed", requestId };
}
